import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Download, Loader2, Users } from "lucide-react";
import adminApi, { apiError } from "@/lib/adminApi";

const inputClass =
  "bg-noir-surface border border-noir-border text-white px-4 py-3 text-sm focus:border-gold outline-none";

const PayBadge = ({ status }) => (
  <span className={`text-[10px] font-bold uppercase tracking-[0.15em] border px-2 py-0.5 ${
    status === "paid" ? "text-gold border-gold/40" : status === "failed" ? "text-rouge-hover border-rouge/40" : "text-white/50 border-white/20"
  }`}>
    {status === "paid" ? "Payé" : status === "failed" ? "Échoué" : "Non payé"}
  </span>
);

const AdminRegistrations = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("confirmed");
  const [stages, setStages] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [stageId, setStageId] = useState(searchParams.get("stage") || "");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [confirmExport, setConfirmExport] = useState(false);

  useEffect(() => {
    adminApi.get("/stages").then(({ data }) => setStages(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (tab === "pending") params.registration_status = "pending_payment";
    if (stageId) params.stage_id = stageId;
    if (category) params.category = category;
    if (q) params.q = q;
    adminApi.get("/registrations", { params })
      .then(({ data }) => setRegistrations(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [tab, stageId, category, q]);

  const exportCount = useMemo(
    () => registrations.filter((r) => r.registration_status === "confirmed" && r.payment_status === "paid").length,
    [registrations]
  );

  const downloadExcel = async () => {
    if (!stageId) return;
    setExporting(true);
    try {
      const res = await adminApi.get("/registrations/export.xlsx", {
        params: { stage_id: stageId },
        responseType: "blob",
      });
      const dispo = res.headers["content-disposition"] || "";
      const match = dispo.match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "participants.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé.");
    } catch (e) {
      toast.error(apiError(e, "Export impossible."));
    } finally {
      setExporting(false);
    }
  };

  const categories = useMemo(() => {
    const stage = stages.find((s) => s.id === stageId);
    return stage ? stage.categories : [];
  }, [stages, stageId]);

  return (
    <div data-testid="admin-registrations" className="space-y-6">
      <h1 className="font-display uppercase text-4xl tracking-wide">Inscriptions</h1>

      {/* Export */}
      <div className="bg-noir-surface border border-gold/25 p-5 flex flex-wrap items-center gap-4">
        <select data-testid="export-stage-select" value={stageId} onChange={(e) => setStageId(e.target.value)} className={`${inputClass} flex-1 min-w-[220px]`}>
          <option value="">Stage : sélectionner un stage…</option>
          {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {stageId && tab === "confirmed" && (
          <span data-testid="export-count" className="text-xs text-white/50">{exportCount} participant(s) confirmé(s)</span>
        )}
        <button
          data-testid="export-excel-button"
          onClick={() => setConfirmExport(true)}
          disabled={!stageId || exporting}
          className="inline-flex items-center gap-2 px-6 py-3 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Télécharger Excel
        </button>
        {confirmExport && (
          <div data-testid="export-confirm" className="w-full flex flex-wrap items-center gap-4 border border-gold/30 bg-gold/5 px-5 py-4">
            <p className="text-sm text-white/70">
              Télécharger les données des participants de ce stage ?{" "}
              <strong className="text-gold">{exportCount} participant(s) confirmé(s)</strong> seront inclus
              (données confidentielles — réservé au staff).
            </p>
            <div className="flex gap-3 ml-auto">
              <button
                data-testid="export-confirm-yes"
                onClick={() => { setConfirmExport(false); downloadExcel(); }}
                className="px-5 py-2.5 bg-rouge text-white text-[11px] font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover"
              >
                Confirmer
              </button>
              <button
                data-testid="export-confirm-no"
                onClick={() => setConfirmExport(false)}
                className="px-5 py-2.5 border border-white/20 text-white text-[11px] font-bold uppercase tracking-[0.15em] rounded-full hover:border-white"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tabs + filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex border border-noir-border">
          {[["confirmed", "Confirmés"], ["pending", "En attente de paiement"]].map(([key, label]) => (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
                tab === key ? "bg-gold text-noir" : "text-white/50 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            data-testid="registrations-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un participant, une référence…"
            className={`${inputClass} w-full pl-10`}
          />
        </div>
        <select data-testid="registrations-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
          <option value="">Toutes catégories</option>
          {(categories.length ? categories : ["U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18"]).map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {loading && <p className="text-white/40 text-sm">Chargement…</p>}

      {/* Table desktop */}
      {!loading && (
        <div className="hidden md:block overflow-x-auto border border-noir-border">
          <table data-testid="registrations-table" className="w-full text-sm">
            <thead>
              <tr className="bg-noir-surface text-left text-[10px] uppercase tracking-[0.2em] text-white/40">
                {["Nom", "Prénom", "Catégorie", "Naissance", "Club", "Email", "Téléphone", "Poste", "Tenue (M/S/Ch)", "Droit image", "Paiement", "Inscription"].map((h) => (
                  <th key={h} className="px-4 py-3 font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {registrations.map((r) => (
                <tr key={r.id} className="hover:bg-white/[0.03] cursor-pointer transition-colors" onClick={() => (window.location.href = `/admin/inscriptions/${r.id}`)} data-testid={`reg-row-${r.id}`}>
                  <td className="px-4 py-3 font-semibold">{r.child_last_name}</td>
                  <td className="px-4 py-3">{r.child_first_name}</td>
                  <td className="px-4 py-3 text-gold/80">{r.category}</td>
                  <td className="px-4 py-3 text-white/60">{r.child_birth_date}</td>
                  <td className="px-4 py-3 text-white/60">{r.current_club || "—"}</td>
                  <td className="px-4 py-3 text-white/60">{r.email}</td>
                  <td className="px-4 py-3 text-white/60">{r.mother_phone || r.father_phone || "—"}</td>
                  <td className="px-4 py-3 text-white/60">{r.player_type === "gardien" ? "Gardien" : (r.playing_position || "Joueur")}</td>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap" data-testid={`reg-sizes-${r.id}`}>
                    {r.jersey_size || r.short_size || r.sock_size
                      ? `${r.jersey_size || "—"} / ${r.short_size || "—"} / ${r.sock_size || "—"}`
                      : "—"}
                  </td>
                  <td className="px-4 py-3" data-testid={`reg-image-rights-${r.id}`}>
                    {r.image_rights_accepted ? (
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em] border px-2 py-0.5 text-gold border-gold/40">Photos OK</span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em] border px-2 py-0.5 text-rouge-hover border-rouge/40">Photos NON</span>
                    )}
                  </td>
                  <td className="px-4 py-3"><PayBadge status={r.payment_status} /></td>
                  <td className="px-4 py-3 text-white/40 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {registrations.length === 0 && <p className="p-6 text-white/40 text-sm">Aucune inscription trouvée.</p>}
        </div>
      )}

      {/* Cartes mobile */}
      {!loading && (
        <div className="md:hidden space-y-3">
          {registrations.map((r) => (
            <Link key={r.id} to={`/admin/inscriptions/${r.id}`} data-testid={`reg-card-${r.id}`} className="block bg-noir-surface border border-noir-border p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold">{r.child_first_name} {r.child_last_name}</p>
                <PayBadge status={r.payment_status} />
              </div>
              <p className="mt-1 text-xs text-white/50">{r.stage_name} — {r.category}</p>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-wider" data-testid={`reg-card-image-rights-${r.id}`}>
                {r.image_rights_accepted ? <span className="text-gold">Photos : acceptées</span> : <span className="text-rouge-hover">Photos : non acceptées</span>}
              </p>
              {(r.jersey_size || r.short_size || r.sock_size) && (
                <p className="mt-1 text-xs text-white/60">Tenue : M {r.jersey_size || "—"} · S {r.short_size || "—"} · Ch {r.sock_size || "—"}</p>
              )}
              <p className="mt-1 text-[10px] text-white/35 uppercase tracking-wider">{r.registration_reference}</p>
            </Link>
          ))}
          {registrations.length === 0 && <p className="text-white/40 text-sm">Aucune inscription trouvée.</p>}
        </div>
      )}
    </div>
  );
};

export default AdminRegistrations;
